import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axiosInstance from '../utils/axios';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState({ open: false, log: null });
  
  // Фильтры
  const [filters, setFilters] = useState({
    userId: '',
    action: '',
    resource: '',
    status: '',
    startDate: null,
    endDate: null,
    search: ''
  });

  // Список пользователей для фильтра
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchLogs();
    fetchUsers();
    fetchStats();
  }, [page, rowsPerPage, filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters,
        startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
        endDate: filters.endDate ? filters.endDate.toISOString() : undefined
      };

      const response = await axiosInstance.get('/activity-logs', { params });
      setLogs(response.data.logs);
      setTotal(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // Получаем всех пользователей через эндпоинт staff (все роли)
      const response = await axiosInstance.get('/staff');
      
      if (response.data && Array.isArray(response.data)) {
        setUsers(response.data);
      } else if (response.data && response.data.users && Array.isArray(response.data.users)) {
        setUsers(response.data.users);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setUsers(response.data.data);
      } else {
        console.warn('Unexpected users response format:', response.data);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/activity-logs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleResetFilters = () => {
    setFilters({
      userId: '',
      action: '',
      resource: '',
      status: '',
      startDate: null,
      endDate: null,
      search: ''
    });
    setPage(0);
  };

  const handleExport = async () => {
    try {
      const params = {
        ...filters,
        startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
        endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
        format: 'csv'
      };

      const response = await axiosInstance.get('/activity-logs/export', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `activity-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting logs:', error);
    }
  };

  const getActionColor = (action) => {
    const colors = {
      CREATE: 'success',
      UPDATE: 'info',
      DELETE: 'error',
      VIEW: 'default',
      LOGIN: 'primary',
      LOGOUT: 'default',
      APPROVE: 'success',
      REJECT: 'error',
      BLOCK: 'error',
      UNBLOCK: 'success',
      EXPORT: 'warning',
      SEND_MESSAGE: 'info',
      BROADCAST: 'warning',
      CHANGE_STATUS: 'info',
      CHANGE_BALANCE: 'warning'
    };
    return colors[action] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      SUCCESS: 'success',
      FAILED: 'warning',
      ERROR: 'error'
    };
    return colors[status] || 'default';
  };

  const actionTypes = [
    'CREATE', 'UPDATE', 'DELETE', 'VIEW',
    'LOGIN', 'LOGOUT',
    'APPROVE', 'REJECT', 'BLOCK', 'UNBLOCK',
    'EXPORT', 'SEND_MESSAGE', 'BROADCAST',
    'CHANGE_STATUS', 'CHANGE_BALANCE'
  ];

  const resourceTypes = [
    'users', 'addresses', 'orders', 'cities', 'districts',
    'products', 'packagings', 'telegramBots', 'telegramUsers',
    'telegramChannels', 'withdrawals', 'stats', 'inventory',
    'couriers', 'roles', 'settings'
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">
          Логи активности
        </Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchLogs}
            sx={{ mr: 1 }}
          >
            Обновить
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            variant="contained"
          >
            Экспорт
          </Button>
        </Box>
      </Box>

      {/* Статистика */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Всего логов
                </Typography>
                <Typography variant="h4">
                  {stats.totalLogs}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Успешных
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.statusStats.find(s => s._id === 'SUCCESS')?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Ошибок
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.statusStats.find(s => s._id === 'ERROR')?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Активных пользователей
                </Typography>
                <Typography variant="h4">
                  {stats.topUsers.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Фильтры */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Фильтры
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Пользователь"
                value={filters.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
                SelectProps={{
                  displayEmpty: true
                }}
              >
                <MenuItem value="">Все пользователи ({users.length})</MenuItem>
                {users
                  .sort((a, b) => a.username.localeCompare(b.username))
                  .map(user => {
                    const roleIcons = {
                      curator: '👑',
                      operator: '👨‍💼',
                      courier: '🚚'
                    };
                    return (
                      <MenuItem key={user._id} value={user._id}>
                        {roleIcons[user.role] || '👤'} {user.username} ({user.role})
                      </MenuItem>
                    );
                  })}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Действие"
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <MenuItem value="">Все</MenuItem>
                {actionTypes.map(action => (
                  <MenuItem key={action} value={action}>{action}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Ресурс"
                value={filters.resource}
                onChange={(e) => handleFilterChange('resource', e.target.value)}
              >
                <MenuItem value="">Все</MenuItem>
                {resourceTypes.map(resource => (
                  <MenuItem key={resource} value={resource}>{resource}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Статус"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">Все</MenuItem>
                <MenuItem value="SUCCESS">Успешно</MenuItem>
                <MenuItem value="FAILED">Ошибка</MenuItem>
                <MenuItem value="ERROR">Критическая ошибка</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Дата от"
                value={filters.startDate}
                onChange={(date) => handleFilterChange('startDate', date)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Дата до"
                value={filters.endDate}
                onChange={(date) => handleFilterChange('endDate', date)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Поиск"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Поиск по описанию, пользователю..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleResetFilters}
                sx={{ height: '56px' }}
              >
                Сбросить
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Таблица логов */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell>Дата</TableCell>
                <TableCell>Пользователь</TableCell>
                <TableCell>Действие</TableCell>
                <TableCell>Ресурс</TableCell>
                <TableCell>Описание</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>IP</TableCell>
                <TableCell width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Alert severity="info">Логи не найдены</Alert>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <React.Fragment key={log._id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => setExpandedRow(expandedRow === log._id ? null : log._id)}
                        >
                          {expandedRow === log._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {format(new Date(log.createdAt), 'dd.MM.yyyy HH:mm:ss', { locale: ru })}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {log.userInfo?.username || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {log.userInfo?.role}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.action}
                          color={getActionColor(log.action)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{log.resource}</TableCell>
                      <TableCell>{log.description}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          color={getStatusColor(log.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {log.ipAddress || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => setDetailsDialog({ open: true, log })}
                        >
                          <InfoIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 0 }}>
                        <Collapse in={expandedRow === log._id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Метод: <strong>{log.method}</strong>
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom>
                                  Эндпоинт: <strong>{log.endpoint}</strong>
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom>
                                  User Agent: <Typography variant="caption">{log.userAgent}</Typography>
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                {log.duration && (
                                  <Typography variant="subtitle2" gutterBottom>
                                    Длительность: <strong>{log.duration}ms</strong>
                                  </Typography>
                                )}
                                {log.errorMessage && (
                                  <Alert severity="error" sx={{ mt: 1 }}>
                                    {log.errorMessage}
                                  </Alert>
                                )}
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
        />
      </Card>

      {/* Диалог с деталями */}
      <Dialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, log: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Детали лога</DialogTitle>
        <DialogContent>
          {detailsDialog.log && (
            <Box>
              <pre style={{ overflow: 'auto', background: '#f5f5f5', padding: '16px', borderRadius: '4px' }}>
                {JSON.stringify(detailsDialog.log, null, 2)}
              </pre>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog({ open: false, log: null })}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityLogs;
